import {Button, Group, Stack, Text} from '@mantine/core';
import {useCallback} from 'react';
import {SubmitHandler} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {ActionClassService} from '../api/generated/ActionClassService.ts';
import {ActionCodesService} from '../api/generated/ActionCodesService.ts';
import {CodesService} from '../api/generated/CodesService.ts';
import {AsyncStateContainer} from '../components/async-state-container.tsx';
import {BasicPage} from '../components/basic-page';
import {BreadcrumbPart} from '../components/page-title.tsx';
import {createForm} from '../forms';
import {useNotification} from '../hooks/use-notifications.tsx';
import {useOptions} from '../hooks/use-options.ts';
import {ROUTES} from '../routes';
import {actionClassSchema, ActionClassSchema} from './schemas.ts';
import {DeleteButton} from '../components/delete-button.tsx';

const breadcrumbsCreate: BreadcrumbPart[] = [
  {title: 'Action Classes', href: ROUTES.ADMIN.ACTION_CLASSES.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<ActionClassSchema>();

const codeDefaultValues: ActionClassSchema = {
  class: '',
  codeId: 0,
  actionCodeId: 0,
  canBeChanged: false,
};

export const ActionClassCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<ActionClassSchema>>(
    async (value) => {
      const response = await ActionClassService.create({body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Action Class Created');
      navigate(ROUTES.ADMIN.ACTION_CLASSES.INDEX.path);
    },
    [navigate, notifications]
  );

  return (
    <BasicPage title={breadcrumbsCreate}>
      <Form
        defaultValues={codeDefaultValues}
        schema={actionClassSchema}
        onSubmit={onSubmit}
      >
        <Form.Container>
          <FormFields />
          <Group mt={24} justify="flex-end">
            <Button
              type="button"
              variant="outline"
              component={Link}
              to={ROUTES.ADMIN.ACTION_CLASSES.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Action Class</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

const breadcrumbsUpdate: BreadcrumbPart[] = [
  {title: 'Action Classes', href: ROUTES.ADMIN.ACTION_CLASSES.INDEX.path},
  {title: 'Update'},
];

export const ActionClassUpdate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.ACTION_CLASSES.DETAIL);

  const fetchActionClass = useAsync(async () => {
    const {data} = await ActionClassService.getById({
      id,
    });
    return data;
  }, [id]);

  const actionClass = fetchActionClass.value || undefined;

  const onSubmit = useCallback<SubmitHandler<ActionClassSchema>>(
    async (value) => {
      const response = await ActionClassService.update({id, body: value});

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Action Class Updated');
      navigate(ROUTES.ADMIN.ACTION_CLASSES.INDEX.path);
    },
    [id, navigate, notifications]
  );

  const [deleteActionClassState, deleteActionClass] = useAsyncFn(async () => {
    const response = await ActionClassService.delete({id});

    if (response.hasErrors) {
      notifications.error('Unable to delete action class');
      return;
    }

    notifications.success('Action class deleted');
    navigate(ROUTES.ADMIN.ACTION_CLASSES.INDEX.path);
  }, [id, navigate, notifications]);

  return (
    <BasicPage title={breadcrumbsUpdate}>
      <AsyncStateContainer {...fetchActionClass}>
        <Form
          schema={actionClassSchema}
          onSubmit={onSubmit}
          defaultValues={actionClass}
        >
          <Form.Container>
            <FormFields />
            <Group mt={24} justify="space-between">
              <DeleteButton
                onConfirm={deleteActionClass}
                buttonLabel="Delete"
                confirmButtonLabel="Delete Action Class"
                loading={deleteActionClassState.loading}
                confirmTitle={
                  <Text fw={600}>
                    Are you sure you want to delete this action class?
                  </Text>
                }
              />
              <Group>
                <Button
                  type="button"
                  variant="outline"
                  component={Link}
                  to={ROUTES.ADMIN.ACTION_CLASSES.INDEX.path}
                >
                  Cancel
                </Button>
                <Form.Submit>Update Action Class</Form.Submit>
              </Group>
            </Group>
          </Form.Container>
        </Form>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields = () => {
  const actionCodeOptions =
    useOptions(ActionCodesService.getActionCodeOptions).value ?? [];
  const levelOfServiceCodeOptions =
    useOptions(CodesService.getLevelOfServiceCodeOptions).value ?? [];
  const partClassOptions =
    useOptions(ActionClassService.getPartClassOptions).value ?? [];

  return (
    <Form.Row>
      <Stack>
        <Form.Select
          name="actionCodeId"
          label="Action Code"
          placeholder="Action Code"
          data={actionCodeOptions}
          searchable
        />
        <Form.Checkbox name="canBeChanged" label="Can Be Changed" />
      </Stack>
      <Form.Select
        name="codeId"
        label="Level Of Service"
        placeholder="Level Of Service"
        data={levelOfServiceCodeOptions}
        searchable
      />
      <Form.Select
        name="class"
        label="Part Class"
        placeholder="Part Class"
        data={partClassOptions}
        searchable
      />
    </Form.Row>
  );
};
